import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
    Form,
    Button,
    Spinner,
    Alert,
    InputGroup,
    Dropdown,
    OverlayTrigger,
    Tooltip,
} from "react-bootstrap";
import { FaEye, FaEyeSlash, FaExchangeAlt } from "react-icons/fa";

const ResetPassword = () => {
    const [view, setView] = useState("customer");
    const [idNumber, setIdNumber] = useState("");
    const [customer, setCustomer] = useState(null);
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [customerMessage, setCustomerMessage] = useState(null);
    const [customerError, setCustomerError] = useState(null);
    const [shake, setShake] = useState(false);
    const [staffList, setStaffList] = useState([]);
    const [selectedStaff, setSelectedStaff] = useState(null);
    const [staffPassword, setStaffPassword] = useState("");
    const [staffConfirmPassword, setStaffConfirmPassword] = useState("");
    const [showStaffPassword, setShowStaffPassword] = useState(false);
    const [showStaffConfirmPassword, setShowStaffConfirmPassword] = useState(false);
    const [staffMessage, setStaffMessage] = useState(null);
    const [staffError, setStaffError] = useState(null);
    const [shakeStaff, setShakeStaff] = useState(false);
    const [loading, setLoading] = useState(false);
    const staffFetched = useRef(false);

    const fetchStaff = async () => {
        try {
            const res = await axios.get(
                "https://www.azzenterprises.com/api/admin/getallusers",
                { withCredentials: true }
            );
            setStaffList(res.data || []);
        } catch (err) {
            console.error("Failed to load staff list", err);
        }
    };

    useEffect(() => {
        if (!staffFetched.current) {
            fetchStaff();
            staffFetched.current = true;
        }
    }, []);

    const fetchCustomer = async () => {
        try {
            setLoading(true);
            setCustomerError(null);
            setCustomerMessage(null);
            const res = await axios.get(
                `https://www.azzenterprises.com/api/customers/findById/${idNumber}`
            );
            setCustomer(res.data);
        } catch {
            setCustomerError("Customer not found. Please check ID number.");
            setCustomer(null);
        } finally {
            setLoading(false);
        }
    };

    const handleCustomerSubmit = async (e) => {
        e.preventDefault();
        if (password !== confirmPassword) {
            setCustomerError("Passwords do not match!");
            setShake(true);
            setTimeout(() => setShake(false), 500);
            return;
        }

        try {
            setLoading(true);
            setCustomerError(null);
            setCustomerMessage(null);
            await axios.put(
                `https://www.azzenterprises.com/api/customers/reset-password/${idNumber}`,
                { password },
                { withCredentials: false }
            );
            setCustomerMessage("Customer password updated successfully!");
            setPassword("");
            setConfirmPassword("");
            setIdNumber("");
            setCustomer(null);
        } catch {
            setCustomerError("Failed to update password. Try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleStaffSubmit = async (e) => {
        e.preventDefault();
        if (staffPassword !== staffConfirmPassword) {
            setStaffError("Passwords do not match!");
            setShakeStaff(true);
            setTimeout(() => setShakeStaff(false), 500);
            return;
        }

        try {
            setLoading(true);
            setStaffError(null);
            setStaffMessage(null);
            await axios.put(
                `https://www.azzenterprises.com/api/admin/reset-staff-password/${selectedStaff?.id}`,
                { password: staffPassword },
                { withCredentials: true }
            );
            setStaffMessage("Staff password updated successfully!");
            setStaffPassword("");
            setStaffConfirmPassword("");
            setSelectedStaff(null);
        } catch {
            setStaffError("Failed to update staff password. Try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className="d-flex justify-content-center align-items-center bg-light"
            style={{ padding: "20px", minHeight: "50vh" }}
        >
            <div className="container-sm" style={{ maxWidth: "580px" }}>
                <div className="card shadow-sm rounded-4 w-100">
                    <div className="card-body p-4 position-relative small-text">

                        <OverlayTrigger
                            placement="left"
                            overlay={
                                <Tooltip id="switch-tooltip">
                                    Switch to {view === "customer" ? "Staff" : "Customer"} View
                                </Tooltip>
                            }
                        >
                            <Button
                                variant="success"
                                style={{
                                    position: "absolute",
                                    top: "12px",
                                    right: "12px",
                                    zIndex: 10,
                                    padding: "6px 8px",
                                }}
                                onClick={() =>
                                    setView(view === "customer" ? "staff" : "customer")
                                }
                            >
                                <FaExchangeAlt />
                            </Button>
                        </OverlayTrigger>

                        {view === "customer" && (
                            <>
                                <h6 className="fw-bold mb-3 text-success">
                                    Reset Customer Password
                                </h6>
                                <Form onSubmit={handleCustomerSubmit}>
                                    <Form.Group className="mb-2">
                                        <Form.Label>ID Number</Form.Label>
                                        <InputGroup>
                                            <Form.Control
                                                type="text"
                                                size="sm"
                                                value={idNumber}
                                                onChange={(e) => setIdNumber(e.target.value)}
                                                placeholder="Enter customer ID number"
                                                required
                                            />
                                            <Button
                                                size="sm"
                                                variant="outline-success"
                                                onClick={fetchCustomer}
                                                disabled={!idNumber || loading}
                                            >
                                                {loading ? (
                                                    <Spinner animation="border" size="sm" />
                                                ) : (
                                                    "Search"
                                                )}
                                            </Button>
                                        </InputGroup>
                                    </Form.Group>

                                    {customer && (
                                        <>
                                            <Alert
                                                variant="info"
                                                className="rounded-3 py-1 my-2 small"
                                            >
                                                Customer: {customer.full_name}
                                            </Alert>

                                            <Form.Group className="mb-2">
                                                <Form.Label>New Password</Form.Label>
                                                <InputGroup>
                                                    <Form.Control
                                                        size="sm"
                                                        type={showPassword ? "text" : "password"}
                                                        value={password}
                                                        onChange={(e) =>
                                                            setPassword(e.target.value)
                                                        }
                                                        placeholder="Enter new password"
                                                        required
                                                    />
                                                    <Button
                                                        size="sm"
                                                        variant="outline-success"
                                                        onClick={() =>
                                                            setShowPassword(!showPassword)
                                                        }
                                                        type="button"
                                                    >
                                                        {showPassword ? (
                                                            <FaEyeSlash />
                                                        ) : (
                                                            <FaEye />
                                                        )}
                                                    </Button>
                                                </InputGroup>
                                            </Form.Group>

                                            <Form.Group className="mb-3">
                                                <Form.Label>Confirm Password</Form.Label>
                                                <InputGroup>
                                                    <Form.Control
                                                        size="sm"
                                                        type={
                                                            showConfirmPassword
                                                                ? "text"
                                                                : "password"
                                                        }
                                                        value={confirmPassword}
                                                        onChange={(e) =>
                                                            setConfirmPassword(e.target.value)
                                                        }
                                                        placeholder="Confirm new password"
                                                        required
                                                        className={shake ? "shake" : ""}
                                                    />
                                                    <Button
                                                        size="sm"
                                                        variant="outline-success"
                                                        onClick={() =>
                                                            setShowConfirmPassword(
                                                                !showConfirmPassword
                                                            )
                                                        }
                                                        type="button"
                                                    >
                                                        {showConfirmPassword ? (
                                                            <FaEyeSlash />
                                                        ) : (
                                                            <FaEye />
                                                        )}
                                                    </Button>
                                                </InputGroup>
                                            </Form.Group>

                                            <Button
                                                size="sm"
                                                variant="success"
                                                type="submit"
                                                className="w-100 fw-semibold rounded-3 py-1"
                                                disabled={
                                                    loading ||
                                                    !password ||
                                                    !confirmPassword
                                                }
                                            >
                                                {loading ? (
                                                    <Spinner animation="border" size="sm" />
                                                ) : (
                                                    "Update Password"
                                                )}
                                            </Button>
                                        </>
                                    )}

                                    {customerMessage && (
                                        <Alert
                                            variant="success"
                                            className="mt-2 small rounded-3"
                                        >
                                            {customerMessage}
                                        </Alert>
                                    )}
                                    {customerError && (
                                        <Alert
                                            variant="danger"
                                            className="mt-2 small rounded-3"
                                        >
                                            {customerError}
                                        </Alert>
                                    )}
                                </Form>
                            </>
                        )}

                        {view === "staff" && (
                            <>
                                <h6 className="fw-bold mb-3 text-success">
                                    Reset Staff Password
                                </h6>

                                <Dropdown className="mb-2">
                                    <Dropdown.Toggle
                                        variant="outline-success"
                                        className="w-100 text-start"
                                        size="sm"
                                    >
                                        {selectedStaff
                                            ? selectedStaff.user_name
                                            : "Select Staff Member"}
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu
                                        className="w-100"
                                        style={{
                                            maxHeight: "120px",
                                            overflowY: "auto",
                                        }}
                                    >
                                        {staffList.map((staff) => (
                                            <Dropdown.Item
                                                key={staff.id}
                                                active={selectedStaff?.id === staff.id}
                                                onClick={() => setSelectedStaff(staff)}
                                            >
                                                {staff.user_name}
                                            </Dropdown.Item>
                                        ))}
                                    </Dropdown.Menu>
                                </Dropdown>

                                {selectedStaff && (
                                    <Form onSubmit={handleStaffSubmit}>
                                        <Form.Group className="mb-2">
                                            <Form.Label>New Password</Form.Label>
                                            <InputGroup>
                                                <Form.Control
                                                    size="sm"
                                                    type={
                                                        showStaffPassword
                                                            ? "text"
                                                            : "password"
                                                    }
                                                    value={staffPassword}
                                                    onChange={(e) =>
                                                        setStaffPassword(e.target.value)
                                                    }
                                                    placeholder="Enter new password"
                                                    required
                                                />
                                                <Button
                                                    size="sm"
                                                    variant="outline-success"
                                                    onClick={() =>
                                                        setShowStaffPassword(
                                                            !showStaffPassword
                                                        )
                                                    }
                                                    type="button"
                                                >
                                                    {showStaffPassword ? (
                                                        <FaEyeSlash />
                                                    ) : (
                                                        <FaEye />
                                                    )}
                                                </Button>
                                            </InputGroup>
                                        </Form.Group>

                                        <Form.Group className="mb-3">
                                            <Form.Label>Confirm Password</Form.Label>
                                            <InputGroup>
                                                <Form.Control
                                                    size="sm"
                                                    type={
                                                        showStaffConfirmPassword
                                                            ? "text"
                                                            : "password"
                                                    }
                                                    value={staffConfirmPassword}
                                                    onChange={(e) =>
                                                        setStaffConfirmPassword(
                                                            e.target.value
                                                        )
                                                    }
                                                    placeholder="Confirm new password"
                                                    required
                                                    className={shakeStaff ? "shake" : ""}
                                                />
                                                <Button
                                                    size="sm"
                                                    variant="outline-success"
                                                    onClick={() =>
                                                        setShowStaffConfirmPassword(
                                                            !showStaffConfirmPassword
                                                        )
                                                    }
                                                    type="button"
                                                >
                                                    {showStaffConfirmPassword ? (
                                                        <FaEyeSlash />
                                                    ) : (
                                                        <FaEye />
                                                    )}
                                                </Button>
                                            </InputGroup>
                                        </Form.Group>

                                        <Button
                                            size="sm"
                                            variant="success"
                                            type="submit"
                                            className="w-100 fw-semibold rounded-3 py-1"
                                            disabled={
                                                loading ||
                                                !staffPassword ||
                                                !staffConfirmPassword
                                            }
                                        >
                                            {loading ? (
                                                <Spinner animation="border" size="sm" />
                                            ) : (
                                                "Update Staff Password"
                                            )}
                                        </Button>

                                        {staffMessage && (
                                            <Alert
                                                variant="success"
                                                className="mt-2 small rounded-3"
                                            >
                                                {staffMessage}
                                            </Alert>
                                        )}
                                        {staffError && (
                                            <Alert
                                                variant="danger"
                                                className="mt-2 small rounded-3"
                                            >
                                                {staffError}
                                            </Alert>
                                        )}
                                    </Form>
                                )}
                            </>
                        )}

                        <style>
                            {`
                            .small-text {
                                font-size: 0.9rem;
                            }
                            .form-label {
                                font-size: 0.85rem;
                            }
                            .shake {
                                animation: shake 0.3s;
                            }
                            @keyframes shake {
                                0% { transform: translateX(0); }
                                25% { transform: translateX(-4px); }
                                50% { transform: translateX(4px); }
                                75% { transform: translateX(-4px); }
                                100% { transform: translateX(0); }
                            }
                        `}
                        </style>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ResetPassword;
